import { Controller } from "@hotwired/stimulus";

/**
 * Azure AD User Lookup Controller
 *
 * Provides typeahead search for Azure AD directory users.
 * On selection, populates email, first_name, and last_name fields.
 *
 * Usage:
 *   <div data-controller="azure-ad-lookup"
 *        data-azure-ad-lookup-url-value="/admin/events/my-event/integrations/azure_ad_integrations/search_users"
 *        data-azure-ad-lookup-photo-url-value="/admin/events/my-event/integrations/azure_ad_integrations/user_photo"
 *        data-azure-ad-lookup-email-target-value="#email-field-id"
 *        data-azure-ad-lookup-first-name-target-value="#first-name-field-id"
 *        data-azure-ad-lookup-last-name-target-value="#last-name-field-id">
 *     <input type="text" data-azure-ad-lookup-target="input" placeholder="Search Azure AD...">
 *     <ul data-azure-ad-lookup-target="results"></ul>
 *   </div>
 */
export class AzureAdLookupController extends Controller {
  static targets = ["input", "results", "selectedDisplay"];
  static values = {
    url: String,
    photoUrl: String,
    emailTargetSelector: String,
    firstNameTargetSelector: String,
    lastNameTargetSelector: String,
    userIdTargetSelector: String,
    nameTargetSelector: { type: String, default: "" },
    lockFields: { type: Boolean, default: true },
    resetOnEvent: { type: String, default: "" },
    minLength: { type: Number, default: 2 },
    debounceMs: { type: Number, default: 300 },
    autoAdd: { type: Boolean, default: false },
  };

  connect() {
    this.debounceTimer = null;
    this.abortController = null;
    this.selectedUser = null;

    // Bind event handlers
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.boundHandleClickOutside);

    // Listen for external reset event (e.g., after adding an invitee)
    if (this.resetOnEventValue) {
      this.boundHandleResetEvent = this.clearSelection.bind(this);
      document.addEventListener(this.resetOnEventValue, this.boundHandleResetEvent);
    }
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.abortController) {
      this.abortController.abort();
    }
    document.removeEventListener("click", this.boundHandleClickOutside);

    if (this.resetOnEventValue && this.boundHandleResetEvent) {
      document.removeEventListener(this.resetOnEventValue, this.boundHandleResetEvent);
    }
  }

  // Called on input event
  search() {
    const query = this.inputTarget.value.trim();

    // Clear results if query is too short
    if (query.length < this.minLengthValue) {
      this.hideResults();
      return;
    }

    // Debounce the search
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceMsValue);
  }

  async performSearch(query) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      this.showLoading();

      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("q", query);

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.displayResults(data.users || []);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Azure AD lookup error:", error);
      this.displayError("Error searching directory");
    }
  }

  getInitials(displayName) {
    if (!displayName) return "?";
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  }

  displayResults(users) {
    if (users.length === 0) {
      this.resultsTarget.innerHTML = `<li class="px-3 py-2 color-fg-muted">No users found</li>`;
    } else {
      this.resultsTarget.innerHTML = users
        .map(
          (user) => `
        <li class="ActionListItem" role="option" tabindex="-1"
            data-action="click->azure-ad-lookup#selectUser keydown->azure-ad-lookup#handleKeydown"
            data-azure-ad-lookup-user-param='${JSON.stringify(user).replace(/'/g, "&#39;")}'>
          <span class="ActionListContent d-flex flex-items-center">
            <span class="avatar circle mr-3 flex-shrink-0 color-bg-accent-emphasis color-fg-on-emphasis d-flex flex-items-center flex-justify-center" data-user-id="${this.escapeHtml(user.id)}" style="width: 32px; height: 32px; font-size: 12px; font-weight: 600;">
              ${this.escapeHtml(this.getInitials(user.display_name))}
            </span>
            <span class="ActionListItem-label flex-1 overflow-hidden">
              <strong class="d-block text-normal">${this.escapeHtml(user.display_name || "")}</strong>
              <span class="ActionListItem-description color-fg-muted text-small d-block text-truncate">${this.escapeHtml(user.email || "")}</span>
            </span>
          </span>
        </li>
      `
        )
        .join("");

      // Lazy load photos
      this.loadPhotos(users);
    }
    this.showResults();
  }

  async loadPhotos(users) {
    if (!this.photoUrlValue) return;

    for (const user of users) {
      if (!user.id) continue;

      try {
        const url = new URL(this.photoUrlValue, window.location.origin);
        url.searchParams.set("user_id", user.id);

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (!response.ok) continue;

        const data = await response.json();
        if (data.photo && /^(https?:\/\/|data:image\/)/.test(data.photo)) {
          const avatarEl = this.resultsTarget.querySelector(
            `[data-user-id="${this.escapeHtml(user.id)}"]`
          );
          if (avatarEl) {
            const img = document.createElement("img");
            img.src = data.photo;
            img.alt = "";
            // GR-2749: `circle` only — NOT `avatar`. The wrapper span is
            // already the avatar, and primer-avatar-overrides.scss sets
            // `.avatar { margin-right: inherit }`, so a nested `.avatar`
            // copies the wrapper's own mr-3 (16px). A 32px child carrying a
            // 16px margin overflows the 32px flex wrapper, and centring the
            // overflow shifts the thumbnail 8px off to the left.
            img.className = "circle";
            img.style.cssText = "width: 32px; height: 32px; object-fit: cover;";
            avatarEl.textContent = "";
            avatarEl.appendChild(img);
            avatarEl.classList.remove(
              "color-bg-accent-emphasis",
              "color-fg-on-emphasis"
            );
          }
        }
      } catch (error) {
        // Silently fail - keep showing initials
      }
    }
  }

  displayError(message) {
    this.resultsTarget.innerHTML = `
      <li class="px-3 py-2 color-fg-danger">${this.escapeHtml(message)}</li>
    `;
    this.showResults();
  }

  showLoading() {
    this.resultsTarget.innerHTML = `
      <li class="px-3 py-2 color-fg-muted">Searching...</li>
    `;
    this.showResults();
  }

  showResults() {
    this.resultsTarget.classList.remove("d-none");
    this.resultsTarget.setAttribute("aria-hidden", "false");
  }

  hideResults() {
    this.resultsTarget.classList.add("d-none");
    this.resultsTarget.setAttribute("aria-hidden", "true");
  }

  selectUser(event) {
    event.preventDefault();
    const userJson = event.currentTarget.dataset.azureAdLookupUserParam;

    if (!userJson) return;

    const user = JSON.parse(userJson);
    this.selectedUser = user;

    // If auto-add mode, dispatch event to add invitee immediately
    if (this.autoAddValue) {
      this.element.dispatchEvent(
        new CustomEvent("azure-ad:auto-add", {
          bubbles: true,
          detail: {
            email: user.email,
            firstName: user.first_name,
            lastName: user.last_name,
            displayName: user.display_name,
            userId: user.id,
          },
        })
      );

      // Clear and reset for next search
      this.inputTarget.value = "";
      this.hideResults();
      this.selectedUser = null;
      return;
    }

    // Populate the target fields and optionally lock them
    this.populateTargetFields(user);
    if (this.lockFieldsValue) {
      this.lockTargetFields();
    }

    // Update the display with clear button
    this.updateSelectedDisplay(user);

    // Hide search input and results
    this.inputTarget.value = "";
    this.inputTarget.closest(".position-relative").classList.add("d-none");
    this.hideResults();
  }

  populateTargetFields(user) {
    // Email field
    if (
      this.emailTargetSelectorValue &&
      this.emailTargetSelectorValue.length > 0
    ) {
      const emailField = document.querySelector(this.emailTargetSelectorValue);
      if (emailField && user.email) {
        emailField.value = user.email;
        emailField.dispatchEvent(new Event("input", { bubbles: true }));
        emailField.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    // First name field
    if (
      this.firstNameTargetSelectorValue &&
      this.firstNameTargetSelectorValue.length > 0
    ) {
      const firstNameField = document.querySelector(
        this.firstNameTargetSelectorValue
      );
      if (firstNameField && user.first_name) {
        firstNameField.value = user.first_name;
        firstNameField.dispatchEvent(new Event("input", { bubbles: true }));
        firstNameField.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    // Last name field
    if (
      this.lastNameTargetSelectorValue &&
      this.lastNameTargetSelectorValue.length > 0
    ) {
      const lastNameField = document.querySelector(
        this.lastNameTargetSelectorValue
      );
      if (lastNameField && user.last_name) {
        lastNameField.value = user.last_name;
        lastNameField.dispatchEvent(new Event("input", { bubbles: true }));
        lastNameField.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    // User ID field (for tracking Azure AD lookup was used)
    if (
      this.userIdTargetSelectorValue &&
      this.userIdTargetSelectorValue.length > 0
    ) {
      const userIdField = document.querySelector(
        this.userIdTargetSelectorValue
      );
      if (userIdField && user.id) {
        userIdField.value = user.id;
        userIdField.dispatchEvent(new Event("input", { bubbles: true }));
        userIdField.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    // Combined name field (e.g., for invitee forms with a single name input)
    if (
      this.nameTargetSelectorValue &&
      this.nameTargetSelectorValue.length > 0
    ) {
      const nameField = document.querySelector(this.nameTargetSelectorValue);
      if (nameField) {
        const fullName = [user.first_name, user.last_name]
          .filter(Boolean)
          .join(" ");
        if (fullName) {
          nameField.value = fullName;
          nameField.dispatchEvent(new Event("input", { bubbles: true }));
          nameField.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }
  }

  lockTargetFields() {
    this.getTargetFields().forEach((field) => {
      if (field) {
        field.readOnly = true;
        field.classList.add("color-bg-subtle");
      }
    });
  }

  unlockTargetFields() {
    this.getTargetFields().forEach((field) => {
      if (field) {
        field.readOnly = false;
        field.classList.remove("color-bg-subtle");
      }
    });
  }

  getTargetFields() {
    const fields = [];
    if (this.emailTargetSelectorValue) {
      fields.push(document.querySelector(this.emailTargetSelectorValue));
    }
    if (this.firstNameTargetSelectorValue) {
      fields.push(document.querySelector(this.firstNameTargetSelectorValue));
    }
    if (this.lastNameTargetSelectorValue) {
      fields.push(document.querySelector(this.lastNameTargetSelectorValue));
    }
    if (this.userIdTargetSelectorValue) {
      fields.push(document.querySelector(this.userIdTargetSelectorValue));
    }
    if (this.nameTargetSelectorValue) {
      fields.push(document.querySelector(this.nameTargetSelectorValue));
    }
    return fields.filter(Boolean);
  }

  updateSelectedDisplay(user) {
    if (this.hasSelectedDisplayTarget) {
      this.selectedDisplayTarget.innerHTML = `
        <div class="d-flex flex-items-center flex-justify-between p-2 color-bg-subtle rounded-2 border">
          <span class="d-flex flex-items-center">
            <span class="avatar circle mr-3 color-bg-accent-emphasis color-fg-on-emphasis d-flex flex-items-center flex-justify-center" style="width: 32px; height: 32px; font-size: 12px; font-weight: 600;">
              ${this.escapeHtml(this.getInitials(user.display_name))}
            </span>
            <span>
              <strong class="d-block">${this.escapeHtml(user.display_name || "")}</strong>
              <span class="color-fg-muted text-small">${this.escapeHtml(user.email || "")}</span>
            </span>
          </span>
          <button type="button" class="btn-octicon color-fg-muted" data-action="click->azure-ad-lookup#clearSelection" aria-label="Clear selection">
            <svg class="octicon octicon-x" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
              <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L9.06 8l3.22 3.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L8 9.06l-3.22 3.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z"></path>
            </svg>
          </button>
        </div>
      `;
      this.selectedDisplayTarget.classList.remove("d-none");
    }
  }

  clearSelection(event) {
    if (event) event.preventDefault();

    this.selectedUser = null;

    // Clear and unlock target fields
    this.getTargetFields().forEach((field) => {
      if (field) {
        field.value = "";
        field.dispatchEvent(new Event("input", { bubbles: true }));
        field.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
    this.unlockTargetFields();

    // Hide selected display
    if (this.hasSelectedDisplayTarget) {
      this.selectedDisplayTarget.innerHTML = "";
      this.selectedDisplayTarget.classList.add("d-none");
    }

    // Show search input again
    this.inputTarget.closest(".position-relative").classList.remove("d-none");
    this.inputTarget.focus();
  }

  handleKeydown(event) {
    const items = this.resultsTarget.querySelectorAll(
      "li.ActionListItem[role='option']"
    );
    const currentIndex = Array.from(items).indexOf(event.target);

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (currentIndex < items.length - 1) {
          items[currentIndex + 1].focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (currentIndex > 0) {
          items[currentIndex - 1].focus();
        } else {
          this.inputTarget.focus();
        }
        break;
      case "Enter":
        event.preventDefault();
        this.selectUser(event);
        break;
      case "Escape":
        event.preventDefault();
        this.hideResults();
        this.inputTarget.focus();
        break;
    }
  }

  handleInputKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const firstItem = this.resultsTarget.querySelector(
        "li.ActionListItem[role='option']"
      );
      if (firstItem) {
        firstItem.focus();
      }
    } else if (event.key === "Escape") {
      this.hideResults();
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults();
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
